'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiErrorStatus } from '@/lib/api-client'
import { approvalsApi, reviewApi } from '@/lib/ap/review'
import type {
  InvoiceApprovalsOut,
  QueueFilter,
  ReviewDetailOut,
  ReviewNotificationList,
  ReviewQueueItem,
  ReviewSummary,
} from '@/lib/ap/review'

/** How often reviewer data refreshes without a reload (plan 04 section 3.2, the it-auto pattern). */
export const REVIEW_REFRESH_MS = 30_000

export interface Resource<T> {
  data: T | null
  loading: boolean
  error: string | null
  /** HTTP status of the last failure (403 not an approver, 404 not yours), or null. */
  status: number | null
  refresh: () => Promise<void>
}

/**
 * Fetch `load` now, again on window focus and every `intervalMs`; keeps the
 * last good data while a background refresh runs, and drops stale answers.
 * Data, error and status are kept with the `load` they came from: when `load`
 * changes (another invoice, another queue filter) the previous key's data is
 * never shown and the resource reads as loading until the new answer arrives.
 * `enabled: false` (no id yet, or not a role that may call it) does nothing.
 */
function usePolled<T>(
  load: (() => Promise<T>) | null,
  { intervalMs = REVIEW_REFRESH_MS, enabled = true }: { intervalMs?: number; enabled?: boolean } = {}
): Resource<T> {
  const [result, setResult] = useState<{
    key: (() => Promise<T>) | null
    data: T | null
    error: string | null
    status: number | null
  }>({ key: null, data: null, error: null, status: null })
  const seq = useRef(0)

  const refresh = useCallback(async () => {
    if (!load || !enabled) return
    const mine = ++seq.current
    try {
      const next = await load()
      if (mine !== seq.current) return
      setResult({ key: load, data: next, error: null, status: null })
    } catch (err) {
      if (mine !== seq.current) return
      const message = err instanceof Error ? err.message : 'Could not load'
      setResult((prev) => ({
        key: load,
        data: prev.key === load ? prev.data : null,
        error: message,
        status: apiErrorStatus(err),
      }))
    }
  }, [load, enabled])

  useEffect(() => {
    if (!load || !enabled) return
    // Deferred to a microtask so no state is set synchronously in the effect body.
    void Promise.resolve().then(refresh)
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    const timer = intervalMs > 0 ? window.setInterval(onFocus, intervalMs) : null
    return () => {
      window.removeEventListener('focus', onFocus)
      if (timer !== null) window.clearInterval(timer)
    }
  }, [refresh, load, enabled, intervalMs])

  const active = Boolean(load) && enabled
  const current = result.key === load
  return {
    data: current ? result.data : null,
    // A disabled resource is never loading; an active one is until its own key answered.
    loading: active && !current,
    error: current ? result.error : null,
    status: current ? result.status : null,
    refresh,
  }
}

/** `GET /api/review/summary`: counts and the signed-in reviewer's name and title. */
export function useReviewSummary(enabled = true): Resource<ReviewSummary> {
  const load = useCallback(() => reviewApi.summary(), [])
  return usePolled(load, { enabled })
}

/** `GET /api/review/assignments?filter=`: the caller's queue (the server scopes it; admin / AP-Manager see all). */
export function useReviewQueue(filter: QueueFilter): Resource<ReviewQueueItem[]> & { total: number } {
  const [total, setTotal] = useState(0)
  const load = useCallback(async () => {
    const page = await reviewApi.queue({ filter })
    setTotal(page.pagination?.total_items ?? page.data.length)
    return page.data
  }, [filter])
  return { ...usePolled(load), total }
}

/** `GET /api/review/assignments/{id}`: the decision pane payload. */
export function useReviewAssignment(assignmentId: number | null, filter: QueueFilter): Resource<ReviewDetailOut> {
  const load = useCallback(
    () => reviewApi.detail(assignmentId as number, filter),
    [assignmentId, filter]
  )
  return usePolled(assignmentId == null ? null : load)
}

/** `GET /api/invoices/{id}/approvals`: the Workbench banner and the Send for Approval switch. */
export function useInvoiceApprovals(invoiceId: number | null, enabled = true): Resource<InvoiceApprovalsOut> {
  const load = useCallback(() => approvalsApi.list(invoiceId as number), [invoiceId])
  return usePolled(invoiceId == null ? null : load, { enabled })
}

/** `GET /api/review/notifications/unread-count`: the bell and the nav badge (A4). */
export function useReviewUnreadCount(enabled = true): Resource<number> {
  const load = useCallback(() => reviewApi.unreadCount(), [])
  return usePolled(load, { enabled })
}

/** `GET /api/ap/approval-notifications`: decisions on the rounds the signed-in AP user sent (A4, header bell). */
export function useApprovalNotifications(enabled: boolean): Resource<ReviewNotificationList> {
  const load = useCallback(() => approvalsApi.notifications(), [])
  return usePolled(load, { enabled })
}

/** `GET /api/review/notifications`, fetched when the bell opens (A4). */
export function useReviewNotifications(enabled: boolean): Resource<ReviewNotificationList> {
  const load = useCallback(() => reviewApi.notifications(), [])
  return usePolled(load, { enabled, intervalMs: enabled ? REVIEW_REFRESH_MS : 0 })
}
